// src/components/AuthPage.jsx
// Unified auth flow: Firebase Auth → MSAL (if needed) → Dashboard
// Single page, no redirects between steps

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, listAll } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CanvasRevealEffect, MiniNavbar } from './SignInFlow';
import { getEmailAccessErrorMessage } from '../utils/emailAccessErrors';
import { buildInitialSignatureProfileDefaults } from '../utils/signatureBuilder';

export default function AuthPage() {
  const { loginWithMicrosoft, loginWithEmailPassword, currentUser, useEmulators } = useAuth();
  const navigate = useNavigate();

  // Steps: idle, signing-in, needs-email-access, granting-access, checking, admin-consent-required, success, error
  const [step, setStep] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [adminConsentUrl, setAdminConsentUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [testEmail, setTestEmail] = useState('test@example.com');
  const [testPassword, setTestPassword] = useState('testpassword123');
  const adminConsentClientId = (() => {
    try {
      return adminConsentUrl ? new URL(adminConsentUrl).searchParams.get('client_id') : '';
    } catch {
      return '';
    }
  })();
  const adminConsentRequestMessage = `Hi IT Team,

I need to use SiteSift email access for work. Microsoft is asking for tenant admin consent for the SiteSift email access enterprise app, which may appear as EmailAutomation-API${adminConsentClientId ? ` (client ID: ${adminConsentClientId})` : ''}.

Admin Consent URL (click to approve):
${adminConsentUrl}

Permissions requested:
Mail.ReadWrite - read and update mailbox messages for SiteSift workflows
Mail.Send - send approved outreach and replies on behalf of the user
offline_access - keep the mailbox connection working after the first approval

Please approve the email access enterprise app in our organization's Microsoft tenant. If Microsoft shows both a SiteSift login app and a SiteSift email access app, the email access app is the one needed for Mail.ReadWrite, Mail.Send, and offline_access.

Thank you!`;

  // Listen for postMessage from MSAL popup (admin consent required)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'MSAL_ADMIN_CONSENT_REQUIRED') {
        setAdminConsentUrl(event.data.adminConsentUrl || '');
        setStep('admin-consent-required');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Reset to initial state
  const handleReset = () => {
    setStep('idle');
    setErrorMessage('');
  };

  // Step 1: Firebase Auth
  const handleSignIn = async () => {
    setStep('signing-in');
    setErrorMessage('');

    try {
      const { user } = await loginWithMicrosoft();
      const uid = user.uid;
      const email = user.email || null;

      // Create/update user document
      const userRef = doc(db, 'users', uid);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        await setDoc(userRef, {
          email,
          ...buildInitialSignatureProfileDefaults(email, user.displayName || ''),
          hasMsalToken: false,
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        });
      } else {
        await updateDoc(userRef, {
          email,
          lastLoginAt: serverTimestamp(),
        });
      }

      // Check if they need MSAL token.
      //
      // The flag alone is not enough. An account whose saved mailbox link has
      // been removed keeps `hasMsalToken: true` until the mail poller runs for
      // it, and this page would wave it through to a dashboard that can neither
      // send nor read. So a true flag is confirmed against the saved link
      // itself. An empty listing is a definite no; a listing that THROWS is not
      // evidence of anything, so it fails open rather than nagging a working
      // account out of its own dashboard.
      const fresh = await getDoc(userRef);
      const freshData = fresh.data() || {};
      const flagged = freshData.hasMsalToken === true && !freshData.msalReconnectRequired;
      let hasToken = flagged;
      if (flagged) {
        try {
          const cache = await listAll(ref(getStorage(), `msal_caches/${uid}`));
          hasToken = cache.items.length > 0;
        } catch {
          hasToken = true;
        }
      }

      if (hasToken) {
        setStep('success');
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        setStep('needs-email-access');
      }
    } catch (err) {
      console.error('Sign-in error:', err);
      if (err.code === 'auth/popup-closed-by-user' || err.message?.includes('cancelled')) {
        setErrorMessage('Sign-in was cancelled.');
      } else {
        setErrorMessage(err.message || 'Sign-in failed.');
      }
      setStep('error');
    }
  };

  const handleTestSignIn = async (event) => {
    event.preventDefault();
    setStep('signing-in');
    setErrorMessage('');

    try {
      const { user } = await loginWithEmailPassword(testEmail, testPassword);
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        email: user.email || testEmail,
        ...buildInitialSignatureProfileDefaults(user.email || testEmail, user.displayName || ''),
        hasMsalToken: true,
        lastLoginAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      }, { merge: true });

      setStep('success');
      navigate('/dashboard');
    } catch (err) {
      console.error('Test sign-in error:', err);
      setErrorMessage(err.message || 'Test sign-in failed.');
      setStep('error');
    }
  };

  // Step 2: MSAL token (email access)
  const handleGrantEmailAccess = async () => {
    if (!currentUser?.uid) {
      setErrorMessage('Please sign in first.');
      setStep('error');
      return;
    }

    setStep('granting-access');
    setErrorMessage('');

    try {
      const functions = getFunctions();
      const initiateMsalAuth = httpsCallable(functions, 'initiateMsalAuth');
      const result = await initiateMsalAuth({ uid: currentUser.uid });

      if (!result.data?.authUrl) {
        throw new Error('Failed to get authentication URL');
      }

      // Open popup
      const popup = window.open(
        result.data.authUrl,
        'msalAuth',
        'width=500,height=700,left=200,top=100'
      );

      // Poll for popup close
      const pollInterval = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(pollInterval);
          setStep('checking');
          await checkForToken();
        }
      }, 500);

    } catch (err) {
      console.error('MSAL init error:', err);
      setErrorMessage(getEmailAccessErrorMessage(err));
      setStep('error');
    }
  };

  // Check if MSAL token exists
  const checkForToken = async () => {
    try {
      const storage = getStorage();
      const pathRef = ref(storage, `msal_caches/${currentUser.uid}`);
      const result = await listAll(pathRef);

      if (result.items.length > 0) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          hasMsalToken: true,
          msalLinkedAt: serverTimestamp(),
        });
        setStep('success');
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        setErrorMessage('Email access setup was not completed.');
        setStep('error');
      }
    } catch (err) {
      console.error('Token check error:', err);
      setErrorMessage(getEmailAccessErrorMessage(err));
      setStep('error');
    }
  };

  // Styles
  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#000',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    position: 'relative',
    overflow: 'hidden',
  };

  const cardStyle = {
    maxWidth: '440px',
    width: '100%',
    textAlign: 'center',
    position: 'relative',
    zIndex: 10,
  };

  const headingStyle = {
    fontSize: '2.25rem',
    fontWeight: 700,
    color: '#fff',
    marginBottom: '0.5rem',
    letterSpacing: '-0.02em',
  };

  const subtitleStyle = {
    fontSize: '1.125rem',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '2rem',
    lineHeight: 1.5,
  };

  const buttonStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 500,
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '9999px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    marginTop: '1rem',
  };

  const spinnerStyle = {
    width: '24px',
    height: '24px',
    border: '3px solid rgba(255,255,255,0.2)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  };

  const errorBoxStyle = {
    padding: '1rem',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '12px',
    marginBottom: '1.5rem',
    color: '#fca5a5',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    whiteSpace: 'pre-line',
    textAlign: 'left',
  };

  const successIconStyle = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1.5rem',
    boxShadow: '0 0 60px rgba(34, 197, 94, 0.4)',
  };

  const testFormStyle = {
    marginTop: '1.5rem',
    padding: '1rem',
    border: '1px solid rgba(125, 92, 255, 0.35)',
    borderRadius: '16px',
    backgroundColor: 'rgba(125, 92, 255, 0.08)',
    textAlign: 'left',
  };

  const testBannerStyle = {
    marginBottom: '0.75rem',
    color: '#c4b5fd',
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase',
  };

  const testInputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '0.5rem',
    padding: '0.75rem',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: '10px',
    backgroundColor: 'rgba(0,0,0,0.3)',
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
  };

  const MicrosoftLogo = () => (
    <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
      <rect x="17" y="17" width="10" height="10" fill="#FEBA08"/>
      <rect x="5" y="17" width="10" height="10" fill="#05A6F0"/>
      <rect x="17" y="5" width="10" height="10" fill="#80BC06"/>
      <rect x="5" y="5" width="10" height="10" fill="#F25325"/>
    </svg>
  );

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        button:hover:not(:disabled) {
          background-color: rgba(255,255,255,0.1) !important;
          border-color: rgba(255,255,255,0.2) !important;
        }
      `}</style>

      {/* Background canvas for visual effect */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <CanvasRevealEffect
          animationSpeed={3}
          colors={[[255, 255, 255]]}
          dotSize={4}
          reverse={step === 'success'}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at center, rgba(0,0,0,0.85) 0%, transparent 100%)' }} />
      </div>

      <MiniNavbar />
      <div style={cardStyle}>
        {/* Step: Idle - Initial state */}
        {step === 'idle' && (
          <>
            <h1 style={headingStyle}>Get Started</h1>
            <p style={subtitleStyle}>Sign in with your Microsoft account to automate your email outreach.</p>
            <button style={buttonStyle} onClick={handleSignIn}>
              <MicrosoftLogo />
              Continue with Microsoft
            </button>
            {useEmulators && (
              <form
                data-testid="test-login-form"
                className="test-login-form"
                onSubmit={handleTestSignIn}
                style={testFormStyle}
              >
                <div className="test-mode-banner" style={testBannerStyle}>
                  Test Mode - Firebase Emulators
                </div>
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }} htmlFor="test-email">
                  Email
                </label>
                <input
                  id="test-email"
                  type="email"
                  value={testEmail}
                  onChange={(event) => setTestEmail(event.target.value)}
                  style={testInputStyle}
                />
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginTop: '0.75rem' }} htmlFor="test-password">
                  Password
                </label>
                <input
                  id="test-password"
                  type="password"
                  value={testPassword}
                  onChange={(event) => setTestPassword(event.target.value)}
                  style={testInputStyle}
                />
                <button className="test-login-button" type="submit" style={{ ...buttonStyle, marginTop: '1rem' }}>
                  Sign In to Emulator
                </button>
              </form>
            )}
          </>
        )}

        {/* Step: Signing In */}
        {step === 'signing-in' && (
          <>
            <h1 style={headingStyle}>Signing in...</h1>
            <p style={subtitleStyle}>Complete the sign-in in the popup window.</p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0' }}>
              <div style={spinnerStyle} />
            </div>
            <button style={secondaryButtonStyle} onClick={handleReset}>
              ← Back
            </button>
          </>
        )}

        {/* Step: Needs Email Access */}
        {step === 'needs-email-access' && (
          <>
            <h1 style={headingStyle}>One More Step</h1>
            <p style={subtitleStyle}>
              Grant email access so we can send and receive emails on your behalf.
            </p>
            <button style={buttonStyle} onClick={handleGrantEmailAccess}>
              <MicrosoftLogo />
              Grant Email Access
            </button>
            <button style={secondaryButtonStyle} onClick={handleReset}>
              ← Back
            </button>
          </>
        )}

        {/* Step: Granting Access */}
        {step === 'granting-access' && (
          <>
            <h1 style={headingStyle}>Granting Access...</h1>
            <p style={subtitleStyle}>Complete the authorization in the popup window.</p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0' }}>
              <div style={spinnerStyle} />
            </div>
            <div style={{
              padding: '1rem',
              backgroundColor: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '12px',
              marginBottom: '1rem',
              textAlign: 'left',
            }}>
              <p style={{ fontWeight: 500, marginBottom: '0.5rem', color: '#fbbf24', fontSize: '0.875rem' }}>
                Don't see a popup?
              </p>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                Check for a blocked popup icon in your browser's address bar.
              </p>
            </div>
            <button style={secondaryButtonStyle} onClick={handleReset}>
              ← Back
            </button>
          </>
        )}

        {/* Step: Checking */}
        {step === 'checking' && (
          <>
            <h1 style={headingStyle}>Verifying...</h1>
            <p style={subtitleStyle}>Confirming your email access was set up correctly.</p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0' }}>
              <div style={spinnerStyle} />
            </div>
            <button style={secondaryButtonStyle} onClick={handleReset}>
              ← Back
            </button>
          </>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <>
            <div style={successIconStyle}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h1 style={headingStyle}>You're In!</h1>
            <p style={subtitleStyle}>Redirecting to your dashboard...</p>
          </>
        )}

        {/* Step: Admin Consent Required */}
        {step === 'admin-consent-required' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(251, 191, 36, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              fontSize: '2.5rem',
            }}>
              🔒
            </div>
            <h1 style={headingStyle}>Admin Approval Required</h1>
            <p style={{ ...subtitleStyle, marginBottom: '1.5rem' }}>
              Your organization requires IT administrator approval before you can use this app.
            </p>

            <div style={{
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '1.25rem',
              textAlign: 'left',
              marginBottom: '1.5rem',
            }}>
              <p style={{ fontWeight: 600, marginBottom: '0.75rem', color: '#fff', fontSize: '0.875rem' }}>
                Send this to your IT administrator:
              </p>
              <div style={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: '8px',
                padding: '1rem',
                fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {adminConsentRequestMessage}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              <button
                style={{
                  ...buttonStyle,
                  flex: 1,
                  backgroundColor: copied ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                  borderColor: copied ? 'rgba(34, 197, 94, 0.4)' : 'rgba(59, 130, 246, 0.4)',
                }}
                onClick={() => {
                  navigator.clipboard.writeText(adminConsentRequestMessage);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? '✓ Copied!' : '📋 Copy Message'}
              </button>
              <a
                href={`mailto:?subject=SiteSift%20Email%20Access%20Approval%20Request&body=${encodeURIComponent(adminConsentRequestMessage)}`}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  textDecoration: 'none',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                }}
              >
                ✉️ Email IT
              </a>
            </div>

            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '1.5rem' }}>
              Once your IT admin approves, click "Try Again" below.
            </p>

            <button style={buttonStyle} onClick={() => setStep('needs-email-access')}>
              <MicrosoftLogo />
              Try Again
            </button>
            <button style={secondaryButtonStyle} onClick={handleReset}>
              ← Back
            </button>
          </>
        )}

        {/* Step: Error */}
        {step === 'error' && (
          <>
            <h1 style={headingStyle}>Something Went Wrong</h1>
            <div style={errorBoxStyle}>{errorMessage}</div>
            <button style={buttonStyle} onClick={handleReset}>
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
